import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";

import { connect } from "react-redux";
import mapboxgl from "mapbox-gl";
import * as actions from "../../store/actions/actions";
import MapStyleToggle from "./MapStyleToggle/MapStyleToggle";
import { WDQMS_TILES, getPeriodsAsQueryParameters, getDaysInMonth } from "../../utils/general";
import { MAP_STYLE_URLS } from "../../utils/mapStyles";
import {
  MAPBOX_KEY,
  MAPBOX_BACKGROUND,
  getMapStyle,
  getMapStyleUknownStation,
  getMapLegend,
  makeStroke,
  strokeColor,
  clearLegend,
  editMapAttribution
} from "../../utils/map";

import Legend from "./Legend/Legend";
import Spinner from "../UI/Spinner/Spinner";
import Popup from "./Popup/Popup";
import GCOSPopup from "../GCOS/Popup/Popup";
import SearchBox from "./SearchBox/SearchBox";

import legendControlIcon from "../../images/icons/align-justify.svg";

import "./Map.css";
import Chart from "../Chart/Chart";
import { colors } from "../../utils/colors";
import imageWATERMARK from "../../images/icons/test_watermark_resized.png";

const getSortKey = (report, periodType, baseline, date) => {
  if (report === "availability") {
    let partBaseline = 6;
    if (periodType === "daily") partBaseline = 24;
    else if (periodType === "monthly") partBaseline = ["*", getDaysInMonth(date), 24];
    if (baseline === "OSCAR") partBaseline = ["get", "nr_expected"];
    return [
      "case",
      ["==", ["get", "in_oscar"], false], 7,
      ["==", partBaseline, 0], 6,
      [">", ["/", ["get", "nr_received"], partBaseline], 1], 5,
      ["all", [">=", ["/", ["get", "nr_received"], partBaseline], 0.8], ["<=", ["/", ["get", "nr_received"], partBaseline], 1]], 4,
      ["all", [">=", ["/", ["get", "nr_received"], partBaseline], 0.3], ["<", ["/", ["get", "nr_received"], partBaseline], 0.8]], 3,
      ["all", [">", ["/", ["get", "nr_received"], partBaseline], 0], ["<", ["/", ["get", "nr_received"], partBaseline], 0.3]], 2,
      1
    ];
  }
  return ["case", ["==", ["get", "in_oscar"], false], 2, 1];
};

mapboxgl.accessToken = MAPBOX_KEY;

const popup = new mapboxgl.Popup();

const Map = props => {
  let prefix = "";
  if (props.baseline === "GBON") {
    prefix = "gbon_";
  }
  const ftype = props[prefix + props.fileType];
  const [layerIsLoaded, setLayerIsLoaded] = useState(true);
  const [mapOpacity, setMapOpacity] = useState(1);
  const [visible, setVisible] = useState(false);
  const [legendVisible, setLegendVisible] = useState(false);
  const [legend, setLegend] = useState(
    getMapLegend(
      prefix,
      props.fileType,
      ftype.selectedReport,
      ftype.selectedVariable,
      ftype.selectedPeriodType,
      ftype.selectedDate,
      ftype.baseline
    )
  );
  const [done, setDone] = useState(false);
  const [firstLayerAdded, setFirstLayerAdded] = useState(false);
  const [currentMapStyle, setCurrentMapStyle] = useState("default");
  const handleMapStyleChange = styleId => {
    if (props.map && styleId !== currentMapStyle) {
      // Save current camera position
      const currentCenter = props.map.getCenter();
      const currentZoom = props.map.getZoom();
      const currentBearing = props.map.getBearing();
      const currentPitch = props.map.getPitch();

      // Update the map style
      props.map.setStyle(MAP_STYLE_URLS[styleId]);

      // Update state
      setCurrentMapStyle(styleId);

      // When the style is loaded, restore camera and add layers
      props.map.once("style.load", () => {
        // Restore camera position
        props.map.setCenter(currentCenter);
        props.map.setZoom(currentZoom);
        props.map.setBearing(currentBearing);
        props.map.setPitch(currentPitch);

        // Re-add the current layer if one exists
        if (firstLayerAdded) {
          addLayer(
            props.map,
            ftype.selectedPeriodType,
            ftype.selectedReport,
            ftype.selectedVariable,
            ftype.selectedCenter
          );
        }
      });
    }
  };
  // function called when clicking on the map control to toggle the legend
  const clickOnLegendButton = () => {
    setLegendVisible(prevLegendVisible => !prevLegendVisible);
  };
  const addLayer = (map, periodType, report, variable, center) => {
    popup.remove();

    const style = getMapStyle(
      prefix,
      props.fileType,
      report,
      variable,
      periodType,
      ftype.selectedDate,
      ftype.baseline
    );
    // Create special style for Uknown stations to be hidden in 2-daily and hourly baselines
    const styleUknown = getMapStyleUknownStation(
      prefix,
      props.fileType,
      report,
      variable,
      periodType,
      ftype.selectedDate,
      ftype.baseline
    );
    setLegend(
      getMapLegend(
        prefix,
        props.fileType,
        report,
        variable,
        periodType,
        ftype.selectedDate,
        ftype.baseline
      )
    );

    let layerName;
    let functionName;
    let url;
    let periodsAsQP;

    if (report === "availability") {
      functionName = `${props.fileType}_observationby`;
    } else {
      functionName = `${props.fileType}_${report}by`;
    }
    if (periodType === "six_hour") {
      functionName = `${functionName}_sixhour_period`;
    } else {
      functionName = `${functionName}_${periodType}_period`;
    }
    if (props.baseline === "GBON") {
      functionName = "gbon" + functionName;
    }
    if (props.fileType === "wis2gtssynop" || props.fileType === "wis2gtstemp") {
      if (report == "wis2live") {
        functionName = `${props.fileType}_wis2liveby_${periodType}_period`;
      } else if (report == "gts-to-wis2") {
        functionName = `${props.fileType}_wis2vsgtsby_${periodType}_period`;
      } else if (report == "tac-to-bufr") {
        functionName = `${props.fileType}_bufrvstacby_${periodType}_period`;
      } else if (report == "bufr_oscar_coords") {
        functionName = `${props.fileType}_bufr_oscar_coordsby_${periodType}_period`;
      }
    }
    if (ftype.combinedMode) {
      functionName += "_combined";
    } else {
      functionName += "_by_center";
    }
    if (props.fileType === "guan" || props.fileType === "gsn") {
      const selectedPeriod = ftype.dates[ftype.selectedDate].id;
      periodsAsQP = `period=${selectedPeriod}`;
      if (report === "availability") {
        functionName = `${props.fileType}_observation`;
      } else if (report === "quality") {
        functionName = `${props.fileType}_quality_${variable}`;
      } else if (report === "completeness") {
        functionName = `${props.fileType}_completeness_${variable}`;
      }
    } else {
      let selectedTime = ftype.selectedSixHPeriod;
      if (
        props.fileType === "wis2gtssynop" ||
        props.fileType === "wis2gtstemp"
      ) {
        if (report == "wis2live") {
          selectedTime = ftype.selectedHourlyPeriod;
        } else if (report == "gts-to-wis2") {
          selectedTime = ftype.selectedSixHPeriod;
        } else if (report == "tac-to-bufr") {
          selectedTime = ftype.selectedDate; // only daily period
        } else if (report == "bufr_oscar_coords") {
          selectedTime = ftype.selectedDate; // only daily period
        }
      }
      periodsAsQP = getPeriodsAsQueryParameters(
        ftype.periodData,
        periodType,
        ftype.selectedCenter,
        ftype.selectedDate,
        selectedTime
      );
    }

    layerName = `wdqms-${functionName}`;
    if (report === "quality" && periodType === "alert") {
      url = `${WDQMS_TILES}/tileserv.${functionName}/{z}/{x}/{y}.pbf?param_var_id=${variable}&param_ts=${ftype.selectedDate}&param_center=${center}`;
    } else if (
      props.fileType === "wis2gtssynop" ||
      props.fileType === "wis2gtstemp"
    ) {
      url = `${WDQMS_TILES}/tileserv.${functionName}/{z}/{x}/{y}.pbf?${periodsAsQP.toLowerCase()}`;
    } else {
      url = `${WDQMS_TILES}/tileserv.${functionName}/{z}/{x}/{y}.pbf?param_var_id=${variable}&${periodsAsQP.toLowerCase()}`;
    }
    // update the layer name in the store
    props.onSetActiveLayerName(layerName);
    map.addLayer({
      id: layerName,
      type: "circle",
      source: {
        type: "vector",
        tiles: [url],
        minzoom: 0,
        maxzoom: 16
      },
      "source-layer": layerName,
      layout: {
        "circle-sort-key": getSortKey(report, periodType, ftype.baseline, ftype.selectedDate)
      },
      paint: {
        "circle-radius": {
          base: 1,
          stops: [
            [4, 3],
            [12, 6]
          ]
        },
        "circle-color": style,
        // "circle-opacity": styleUknown, // Just to hide Uknown stations for 2-daily and hourly
        "circle-stroke-width": makeStroke(
          periodType,
          ftype.selectedReport,
          props.fileType
        ),
        "circle-stroke-color": strokeColor(report)
      }
    });
    // When the layer is loaded, set the opacity to 1
    // and remove the spinner
    map.once("idle", function() {
      setMapOpacity(1);
      setLayerIsLoaded(true);
      addEventsOnLayer(map, layerName);
      if (ftype.selectedReport === "timeliness") {
        displayTimelinesCondition();
      }
    });

    clearLegend();
    editMapAttribution(
      props.fileType,
      props.baseline,
      ftype.selectedPeriodType,
      ftype.selectedReport,
      ftype.selectedVariable,
      ftype.selectedCenter,
      ftype.selectedDate,
      ftype.selectedSixHPeriod,
      ftype.selectedHourlyPeriod
    );

    function displayTimelinesCondition() {
      // Check if timeliness data is available for the selected period
      const {
        selectedDate,
        periodData,
        combinedMode,
        selectedPeriodType
      } = ftype;

      // Condition 1: Period is earlier than timeliness deployment date
      if (selectedDate < "2025-09-02") {
        showMessage("Timeliness is not yet available for this period.");
        map.removeLayer(layerName);
        map.removeSource(layerName);
      }

      // Condition 2: In combined mode, check if DWD or ECMWF data is not aggregated for six_hour or daily periods
      else if (
        combinedMode &&
        (selectedPeriodType === "six_hour" || selectedPeriodType === "daily")
      ) {
        const dwdAggregated =
          periodData?.["six_hour"]?.["dates"]?.["DWD"]?.[selectedDate];
        const ecmwfAggregated =
          periodData?.["six_hour"]?.["dates"]?.["ECMWF"]?.[selectedDate];

        if (dwdAggregated === undefined || ecmwfAggregated === undefined) {
          map.removeLayer(layerName);
          map.removeSource(layerName);
        }
      } else {
        hideMessage();
      }
    }
  };

  function onClick(e) {
    const coordinates = e.features[0].geometry.coordinates.slice();
    while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
      coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
    }

    let baseline = null;
    const baselineElement = document.getElementsByClassName(
      "baseline-selected"
    );
    if (baselineElement.length > 0) {
      baseline = baselineElement[0].innerText.replace("*", "");
    }

    // Create a custom container for React content
    const popupContainer = document.createElement("div");

    // Mount the popup with an empty HTML string (we'll use DOM instead)
    popup
      .setLngLat(coordinates)
      .setDOMContent(popupContainer)
      .addTo(props.map);

    let popupComponent;

    if (
      props.fileType === "synop" ||
      props.fileType === "temp" ||
      props.fileType === "buoy" ||
      props.fileType === "ship" ||
      props.fileType === "wis2gtssynop" ||
      props.fileType === "wis2gtstemp"
    ) {
      popupComponent = (
        <Popup
          baselinetxt={baseline}
          obj={popup}
          baseline={props.baseline}
          fileType={props.fileType}
          stationObject={e.features[0].properties}
          combinedMode={ftype.combinedMode}
          setVisible={setVisible}
          selectedReport={ftype.selectedReport}
          selectedPeriodType={ftype.selectedPeriodType}
          selectedCenter={ftype.selectedCenter}
          selectedDate={ftype.selectedDate}
          selectedSixHPeriod={ftype.selectedSixHPeriod}
          selectedHourlyPeriod={ftype.selectedHourlyPeriod}
          selectedVariable={ftype.selectedVariable}
          periodData={ftype.periodData}
          onSetChartData={props.onSetChartData}
          onSetSelectedStation={props.onSetSelectedStation}
          onSetLevel={props.onSetLevel}
        />
      );
    } else if (props.fileType === "guan" || props.fileType === "gsn") {
      popupComponent = (
        <GCOSPopup
          obj={popup}
          fileType={props.fileType}
          stationObject={e.features[0].properties}
          setVisible={setVisible}
          selectedDate={ftype.selectedDate}
          selectedReport={ftype.selectedReport}
          selectedVariable={ftype.selectedVariable}
          onSetChartData={props.onSetChartData}
          onSetSelectedStation={props.onSetSelectedStation}
        />
      );
    }

    // First clean up any previous render (optional safety)
    ReactDOM.unmountComponentAtNode(popupContainer);

    // Now render the component into our custom container
    ReactDOM.render(popupComponent, popupContainer);
  }
  // The event of the former doesn't seem to be removed
  // seems to only occur with combined mode
  const addEventsOnLayer = (map, layerId) => {
    // When a click event occurs on a feature open a popup at the
    // location of the feature, with description HTML from its properties.
    map.on("click", layerId, onClick);

    // Change the cursor to a pointer when the mouse is over the places layer.
    map.on("mouseenter", layerId, function() {
      map.getCanvas().style.cursor = "pointer";
    });

    // Change it back to a pointer when it leaves.
    map.on("mouseleave", layerId, function() {
      map.getCanvas().style.cursor = "";
    });
  };

  // Initialize the map
  useEffect(() => {
    if (!mapboxgl.supported()) {
      // eslint-disable-next-line no-alert
      alert("Your browser does not support Mapbox GL");
    }

    // Calculate longitude based on timezone offset
    const calculateLongitudeFromTimezone = () => {
      // Get timezone offset in minutes
      const timezoneOffsetMinutes = new Date().getTimezoneOffset();
      // Convert to hours (negative because getTimezoneOffset returns the opposite of what we need)
      const timezoneOffsetHours = -timezoneOffsetMinutes / 60;
      // Each timezone is roughly 15 degrees of longitude (360 degrees / 24 hours)
      const longitude = timezoneOffsetHours * 15;

      return longitude;
    };

    // Get longitude based on user's timezone
    const centerLongitude = calculateLongitudeFromTimezone();
    const mapObject = new mapboxgl.Map({
      container: "map",
      style: MAP_STYLE_URLS[currentMapStyle],
      zoom: 1.2,
      center: [centerLongitude, 22], // Center on user's timezone longitude, keep same latitude
      projection: "mercator"
    });

    // new mapboxgl.Marker().setLngLat([centerLongitude, 22]).addTo(mapObject);
    props.onInitializeMap(mapObject);
    editMapAttribution();
    setDone(true);
    // Cleanup function to remove the event listener
    return () => {
      if (props.map !== null) {
        props.map.off(
          "click",
          props.map.getStyle().layers[props.map.getStyle().layers.length - 1]
            .id,
          onClick
        );
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Display the first layer
  useEffect(() => {
    if (
      ftype.readyToFillMenu &&
      done &&
      !firstLayerAdded &&
      props.map !== null
    ) {
      // clear and close the search station control
      props.onChangeStatus({ key: "matches", value: [] });
      props.onChangeStatus({ key: "result", value: "" });
      props.onChangeStatus({
        key: "classContainer",
        value: "inside-filter-closed"
      });
      props.onChangeStatus({ key: "classCCtrlL1", value: "ctrl-search" });

      // If there is no data stop the spinner remove the transparency

      if (
        (props.fileType === "synop" || props.fileType === "temp") &&
        ftype.periodData[ftype.selectedPeriodType].noData
      ) {
        setMapOpacity(1);
        setLayerIsLoaded(true);
      } else {
        setMapOpacity(0.2);
        setLayerIsLoaded(false);

        props.map.on("load", event => {
          addLayer(
            props.map,
            ftype.selectedPeriodType,
            ftype.selectedReport,
            ftype.selectedVariable,
            ftype.selectedCenter
          );

          // Add map control to toggle legend
          const controlTopRight = document.getElementsByClassName(
            "mapboxgl-ctrl-top-right"
          )[0];
          const divMapControl = document.createElement("div");
          divMapControl.className = "mapboxgl-ctrl mapboxgl-ctrl-group";
          divMapControl.innerHTML = `<button class="mapboxgl-ctrl-icon mapboxgl-ctrl-legend" type="button" aria-label="Toggle legend" title="Toggle legend"><img class="control-icon" src="${legendControlIcon}"></button>`;
          controlTopRight.appendChild(divMapControl);

          const legendButton = document.getElementsByClassName(
            "mapboxgl-ctrl-legend"
          )[0];

          legendButton.addEventListener("click", clickOnLegendButton);
          setLegendVisible(true);
          setFirstLayerAdded(true);
        });
      }
    }
    // Cleanup function to remove the event listener
    return () => {
      if (props.map !== null) {
        props.map.off(
          "click",
          props.map.getStyle().layers[props.map.getStyle().layers.length - 1]
            .id,
          onClick
        );
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ftype.readyToFillMenu, done, props.map]);

  function show_watermark() {
    if (window.location.hostname !== "wdqms.wmo.int") {
      const waterMark = Array.from(
        document.querySelectorAll("a.mapboxgl-ctrl-logo")
      );
      waterMark.forEach(element => {
        element.style.backgroundImage = "url(" + imageWATERMARK + ")";
        element.style.width = "140px";
        element.style.height = "41px";
      });
    }
  }

  // Change the layer through the map menu
  useEffect(() => {
    show_watermark();
    if (ftype.readyToFillMenu && firstLayerAdded && props.map !== null) {
      if (props.map.getStyle().layers.length > 1) {
        const obsLayerId = props.map.getStyle().layers[
          props.map.getStyle().layers.length - 1
        ].id;
        // Set the map with transparency and display the spinner
        setMapOpacity(0.2);
        setLayerIsLoaded(false);
        // Only delete the layer if it's one of the WDQMS layer not a background layer
        if (obsLayerId.startsWith("wdqms-")) {
          props.map.removeLayer(obsLayerId);
          props.map.removeSource(obsLayerId);
        }
      }
      // The 2 conditions below are ugly - to improve!
      if (
        props.fileType === "synop" ||
        props.fileType === "temp" ||
        props.fileType === "buoy" ||
        props.fileType === "ship" ||
        props.fileType === "wis2gtssynop" ||
        props.fileType === "wis2gtstemp"
      ) {
        // Only add layer if there is data for that period type
        // useful when changing a period type
        if (!ftype.periodData[ftype.selectedPeriodType].noData) {
          addLayer(
            props.map,
            ftype.selectedPeriodType,
            ftype.selectedReport,
            ftype.selectedVariable,
            ftype.selectedCenter
          );
        }
      } else if (props.fileType === "guan" || props.fileType === "gsn") {
        addLayer(
          props.map,
          ftype.selectedPeriodType,
          ftype.selectedReport,
          ftype.selectedVariable,
          ftype.selectedCenter
        );
      }

      // clear and close the search station control
      props.onChangeStatus({ key: "matches", value: [] });
      props.onChangeStatus({ key: "result", value: "" });
      props.onChangeStatus({
        key: "classContainer",
        value: "inside-filter-closed"
      });
      props.onChangeStatus({ key: "classCCtrlL1", value: "ctrl-search" });

      // log number of layers in the map
    }
    // Cleanup function to remove the event listener
    return () => {
      if (props.map !== null) {
        props.map.off(
          "click",
          props.map.getStyle().layers[props.map.getStyle().layers.length - 1]
            .id,
          onClick
        );
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    ftype.selectedPeriodType,
    ftype.selectedReport,
    ftype.selectedSixHPeriod,
    ftype.selectedVariable,
    ftype.selectedDate,
    ftype.selectedCenter,
    ftype.selectedHourlyPeriod
  ]);

  // Change the layer through the app menu
  useEffect(() => {
    if (ftype.readyToFillMenu && firstLayerAdded) {
      if (props.map.getStyle().layers.length > 1) {
        const obsLayerId = props.map.getStyle().layers[
          props.map.getStyle().layers.length - 1
        ].id;
        // Set the map with transparency and display the spinner
        setMapOpacity(0.2);
        setLayerIsLoaded(false);
        // Only delete the layer if it's one of the WDQMS layer not a background layer
        if (obsLayerId.startsWith("wdqms-")) {
          props.map.removeLayer(obsLayerId);
          props.map.removeSource(obsLayerId);
        }
      }
      // The 2 conditions below are ugly - to improve!
      if (
        props.fileType === "synop" ||
        props.fileType === "temp" ||
        props.fileType === "buoy" ||
        props.fileType === "ship"
      ) {
        // Only add layer if there is data for that period type
        // useful when changing a period type
        if (!ftype.periodData[ftype.selectedPeriodType].noData) {
          addLayer(
            props.map,
            ftype.selectedPeriodType,
            ftype.selectedReport,
            ftype.selectedVariable,
            ftype.selectedCenter
          );
        }
      } else if (props.fileType === "guan" || props.fileType === "gsn") {
        addLayer(
          props.map,
          ftype.selectedPeriodType,
          ftype.selectedReport,
          ftype.selectedVariable,
          ftype.selectedCenter
        );
      }
    }
    // Cleanup function to remove the event listener
    return () => {
      if (props.map !== null) {
        props.map.off(
          "click",
          props.map.getStyle().layers[props.map.getStyle().layers.length - 1]
            .id,
          onClick
        );
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.fileType, ftype.readyToFillMenu, ftype.baseline]);

  // When changing the baseline, update the legend
  useEffect(() => {
    setLegend(
      getMapLegend(
        prefix,
        props.fileType,
        ftype.selectedReport,
        ftype.selectedVariable,
        ftype.selectedPeriodType,
        ftype.selectedDate,
        ftype.baseline
      )
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ftype.baseline]);

  let spinner;
  if (!layerIsLoaded) {
    spinner = <Spinner />;
  }
  let chart;
  if (props.selectedStation !== null) {
    chart = (
      <Chart
        fileType={props.fileType}
        visible={visible}
        setVisible={setVisible}
        baseline={props.baseline}
      />
    );
  }

  return (
    <div id="map-container">
      <div id="map" style={{ opacity: mapOpacity }}>
        <div id="map-overlay" class="hidden">
          <div class="map-overlay-message">
            No data is currently available. Please check back later.
          </div>
        </div>
        <SearchBox fileType={props.fileType} baseline={props.baseline} />
        <Legend
          fileType={props.fileType}
          legendContent={legend}
          isVisible={legendVisible}
          baseline={props.baseline}
        />
        <MapStyleToggle
          currentStyle={currentMapStyle}
          onStyleChange={handleMapStyleChange}
        />
      </div>
      {spinner}
      {chart}
    </div>
  );
};

function showMessage(text) {
  const overlay = document.getElementById("map-overlay");
  const msg = overlay.querySelector(".map-overlay-message");
  msg.textContent = text;
  overlay.classList.remove("hidden");
}

function hideMessage() {
  const overlay = document.getElementById("map-overlay");
  overlay.classList.add("hidden");
}
const mapStateToProps = state => {
  return {
    map: state.map,
    synop: state.synop,
    temp: state.temp,
    gbon_synop: state.gbon_synop,
    gbon_temp: state.gbon_temp,
    guan: state.guan,
    gsn: state.gsn,
    buoy: state.buoy,
    ship: state.ship,
    marine_surface: state.marine_surface,
    wis2gtssynop: state.wis2gtssynop,
    wis2gtstemp: state.wis2gtstemp,
    activeLayerName: state.activeLayerName
  };
};

const mapDispatchToProps = (dispatch, ownProps) => {
  let prefix = "";
  if (ownProps.baseline === "GBON") {
    prefix = "gbon_";
  }

  return {
    onSetSelectedStation: data => {
      dispatch(actions.setSelectedStation(data));
    },
    onSetActiveLayerName: data => {
      dispatch(actions.setActiveLayerName(data));
    },
    onSetChartData: data => {
      dispatch(
        actions.setProperty({
          type: prefix + ownProps.fileType,
          key: "chartData",
          value: data
        })
      );
    },
    onInitializeMap: data => {
      return dispatch((_, getState) => {
        dispatch(actions.initializeMap(data));
        const currentState = getState();
        currentState.map.addControl(
          new mapboxgl.NavigationControl({ showCompass: false })
        );
        currentState.map.addControl(new mapboxgl.FullscreenControl());
        // disable map rotation using right click + drag
        currentState.map.dragRotate.disable();

        // disable map rotation using touch rotation gesture
        currentState.map.touchZoomRotate.disableRotation();
      });
    },
    onSetLevel: data => {
      dispatch(actions.setLevel(data));
    },
    onChangeStatus: data => {
      dispatch(
        actions.setProperty({
          type: "searchedStations",
          key: data.key,
          value: data.value
        })
      );
    }
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(Map);
